import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dumbbell,
  Flag,
  Globe,
  Heart,
  History,
  Leaf,
  Lightbulb,
  Sandwich,
  Search,
  Smile,
  X,
} from "lucide-react";
import { useIdioma } from "../../i18n/IdiomaProvider";
import { RotuloObrigatorio } from "./rotuloObrigatorio";
import { categoriasEmoji, normalizarBusca, type EmojiItem } from "./emojisDados";
import styles from "./SeletorEmoji.module.css";

/* Emojis usados recentemente ficam só no navegador de quem escolheu: é uma
   conveniência de digitação, não um dado do cadastro. */
const CHAVE_RECENTES = "i9:emojis-recentes";
const LIMITE_RECENTES = 16;

const ICONES_CATEGORIA: Record<string, typeof Smile> = {
  recentes: History,
  pessoas: Smile,
  natureza: Leaf,
  comida: Sandwich,
  atividades: Dumbbell,
  viagem: Globe,
  objetos: Lightbulb,
  simbolos: Heart,
  bandeiras: Flag,
};

function lerRecentes(): string[] {
  try {
    const bruto = window.localStorage.getItem(CHAVE_RECENTES);
    const lista = bruto ? (JSON.parse(bruto) as unknown) : [];
    return Array.isArray(lista) ? lista.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function gravarRecentes(emoji: string): string[] {
  const atualizados = [emoji, ...lerRecentes().filter((item) => item !== emoji)].slice(0, LIMITE_RECENTES);
  try {
    window.localStorage.setItem(CHAVE_RECENTES, JSON.stringify(atualizados));
  } catch {
    /* Navegador sem armazenamento (aba anônima/quota): o painel segue funcionando sem histórico. */
  }
  return atualizados;
}

interface GrupoExibido {
  chave: string;
  rotulo: string;
  itens: EmojiItem[];
}

interface PainelEmojisProps {
  /** Emoji atualmente selecionado, destacado na grade. */
  valor?: string;
  onSelecionar: (emoji: string) => void;
  onFechar: () => void;
  /** Abre o painel para cima (usado na caixa de mensagem, que fica no rodapé). */
  paraCima?: boolean;
  /** Alinha o painel pela direita quando o gatilho está próximo da borda. */
  className?: string;
}

/**
 * Painel de seleção de emoji: busca por nome, abas por categoria e histórico
 * de recentes. Não tem estado externo — quem usa decide onde posicioná-lo.
 */
export function PainelEmojis({ valor, onSelecionar, onFechar, paraCima, className }: PainelEmojisProps) {
  const { traduzir } = useIdioma();
  const categorias = categoriasEmoji();
  const [recentes, setRecentes] = useState<string[]>(() => lerRecentes());
  const [aba, setAba] = useState<string>(() => (lerRecentes().length ? "recentes" : "pessoas"));
  const [busca, setBusca] = useState("");
  const listaRef = useRef<HTMLDivElement | null>(null);

  const termo = normalizarBusca(busca);

  const grupos = useMemo<GrupoExibido[]>(() => {
    if (termo) {
      /* Na busca as categorias somem: o resultado vem achatado em um único grupo. */
      const encontrados: EmojiItem[] = [];
      for (const categoria of categorias) {
        for (const item of categoria.itens) {
          if (item.busca.includes(termo)) encontrados.push(item);
        }
      }
      return [{ chave: "resultado", rotulo: "Resultados", itens: encontrados }];
    }

    const gruposRecentes: GrupoExibido[] = recentes.length
      ? [{ chave: "recentes", rotulo: "Recentes", itens: recentes.map((emoji) => ({ emoji, busca: "" })) }]
      : [];

    if (aba === "recentes") {
      /* A aba "Recentes" mostra o histórico seguido das demais categorias, para
         quem abriu o painel sem saber o que procurar. */
      return [...gruposRecentes, ...categorias];
    }

    const selecionada = categorias.find((categoria) => categoria.chave === aba);
    return selecionada ? [selecionada] : categorias;
  }, [aba, categorias, recentes, termo]);

  /* Trocar de aba ou de busca reposiciona a lista no topo. */
  useEffect(() => {
    if (listaRef.current) listaRef.current.scrollTop = 0;
  }, [aba, termo]);

  function escolher(emoji: string) {
    setRecentes(gravarRecentes(emoji));
    onSelecionar(emoji);
  }

  const abas = [
    ...(recentes.length ? [{ chave: "recentes", rotulo: "Recentes" }] : []),
    ...categorias.map((categoria) => ({ chave: categoria.chave, rotulo: categoria.rotulo })),
  ];

  return (
    <div
      className={`${styles.painel} ${paraCima ? styles.painelAcima : styles.painelAbaixo} ${className ?? ""}`}
      role="dialog"
      aria-label={traduzir("Emojis")}
    >
      <div className={styles.painelCabecalho}>
        <h4 className={styles.painelTitulo}>{traduzir("Emojis")}</h4>
        <button type="button" className={styles.fechar} onClick={onFechar} aria-label={traduzir("Fechar")}>
          <X size={16} />
        </button>
      </div>

      <div className={styles.buscaLinha}>
        <span className={styles.buscaIcone}>
          <Search size={14} />
        </span>
        <input
          type="text"
          className={styles.buscaInput}
          value={busca}
          placeholder={traduzir("Nome do emoji")}
          aria-label={traduzir("Buscar emoji pelo nome")}
          onChange={(evento) => setBusca(evento.target.value)}
        />
      </div>

      {!termo && (
        <div className={styles.abas} role="tablist" aria-label={traduzir("Categorias de emoji")}>
          {abas.map((item) => {
            const Icone = ICONES_CATEGORIA[item.chave] ?? Smile;
            const ativa = aba === item.chave;
            return (
              <button
                key={item.chave}
                type="button"
                role="tab"
                aria-selected={ativa}
                data-tooltip={traduzir(item.rotulo)}
                aria-label={traduzir(item.rotulo)}
                className={`${styles.aba} tooltip ${ativa ? styles.abaAtiva : ""}`}
                onClick={() => setAba(item.chave)}
              >
                <Icone size={15} />
              </button>
            );
          })}
        </div>
      )}

      <div className={styles.lista} ref={listaRef}>
        {grupos.every((grupo) => !grupo.itens.length) ? (
          <p className={styles.vazio}>{traduzir("Nenhum emoji encontrado.")}</p>
        ) : (
          grupos.map((grupo) => (
            <section key={grupo.chave}>
              <h5 className={styles.grupoTitulo}>{traduzir(grupo.rotulo)}</h5>
              <div className={styles.grade}>
                {grupo.itens.map((item, indice) => (
                  <button
                    key={`${grupo.chave}-${item.emoji}-${indice}`}
                    type="button"
                    className={`${styles.emoji} tooltip ${valor === item.emoji ? styles.emojiAtivo : ""}`}
                    data-tooltip={item.busca || undefined}
                    aria-label={item.busca || item.emoji}
                    onClick={() => escolher(item.emoji)}
                  >
                    {item.emoji}
                  </button>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

interface SeletorEmojiProps {
  rotulo?: string;
  valor: string;
  onChange: (emoji: string) => void;
  obrigatorio?: boolean;
  erro?: string;
  desabilitado?: boolean;
  /** Abre o painel para cima (rodapés e barras fixas). */
  paraCima?: boolean;
  className?: string;
}

/**
 * Campo de emoji: botão redondo que abre o PainelEmojis.
 * Clicar fora ou pressionar Esc fecha o painel.
 */
export function SeletorEmoji({
  rotulo,
  valor,
  onChange,
  obrigatorio,
  erro,
  desabilitado,
  paraCima,
  className,
}: SeletorEmojiProps) {
  const { traduzir } = useIdioma();
  const [aberto, setAberto] = useState(false);
  const ancoraRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!aberto) return undefined;

    function aoClicarFora(evento: MouseEvent) {
      if (ancoraRef.current && !ancoraRef.current.contains(evento.target as Node)) setAberto(false);
    }
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === "Escape") setAberto(false);
    }

    document.addEventListener("mousedown", aoClicarFora);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("mousedown", aoClicarFora);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aberto]);

  return (
    <div className={`${styles.campo} ${className ?? ""}`}>
      {rotulo && (
        <span className={styles.rotulo}>
          <RotuloObrigatorio rotulo={traduzir(rotulo)} obrigatorio={obrigatorio} />
        </span>
      )}

      <div className={styles.ancora} ref={ancoraRef}>
        <button
          type="button"
          disabled={desabilitado}
          aria-haspopup="dialog"
          aria-expanded={aberto}
          aria-label={traduzir("Escolher emoji")}
          className={`${styles.gatilho} ${valor ? styles.gatilhoAtivo : ""} ${erro ? styles.gatilhoInvalido : ""}`}
          onClick={() => setAberto((atual) => !atual)}
        >
          {valor || <Smile size={18} />}
        </button>

        {aberto && (
          <PainelEmojis
            valor={valor}
            paraCima={paraCima}
            onSelecionar={(emoji) => {
              onChange(emoji === valor ? "" : emoji);
              setAberto(false);
            }}
            onFechar={() => setAberto(false)}
          />
        )}
      </div>

      {erro && <span className={styles.erro}>{traduzir(erro)}</span>}
    </div>
  );
}
